import React, { memo, useCallback, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { COPILOT_PAGE_EVENT, capture } from '../../analytics/posthog';
import V2OnboardingAgent from '../OnboardingAgent/V2OnboardingAgent';
import TalkToReesePage from './TalkToReesePage';
import styles from './CopilotPage.module.css';

const TAB_COPILOT = 'copilot';
const TAB_FINANCE_QA = 'financeQa';

function TabIconSparkles({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path
        className={styles.tabIconFill}
        d="M11 4l1.76 4.74a2 2 0 0 0 1.5 1.5L19 12l-4.74 1.76a2 2 0 0 0-1.5 1.5L11 20l-1.76-4.74a2 2 0 0 0-1.5-1.5L3 12l4.74-1.76a2 2 0 0 0 1.5-1.5z"
      />
      <path d="M19 2.75v3.5M20.75 4.5h-3.5" />
      <circle cx="19.5" cy="19.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function TabIconChatQuestion({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path
        className={styles.tabIconFill}
        d="M20 11.5a7.5 7.5 0 0 1-11.1 6.58L4 19.5l1.76-4.71A7.5 7.5 0 1 1 20 11.5z"
      />
      <path d="M10.4 9.6a2.2 2.2 0 0 1 4.2.9c0 1.45-2.1 1.9-2.1 3" />
      <circle cx="12.5" cy="16.1" r="0.95" fill="currentColor" stroke="none" />
    </svg>
  );
}

const TABS = [
  {
    id: TAB_COPILOT,
    label: 'Financial Copilot',
    panelId: 'copilot-panel-financial',
    tabId: 'copilot-tab-financial',
    Icon: TabIconSparkles,
  },
  {
    id: TAB_FINANCE_QA,
    label: 'Finance Q&A',
    panelId: 'copilot-panel-finance-qa',
    tabId: 'copilot-tab-finance-qa',
    Icon: TabIconChatQuestion,
  },
];

const CopilotTabBar = memo(function CopilotTabBar({ activeTab, onSelect }) {
  return (
    <div className={styles.tabBar} role="tablist" aria-label="Copilot views">
      {TABS.map(({ id, label, panelId, tabId, Icon }) => {
        const selected = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            id={tabId}
            aria-selected={selected}
            aria-controls={panelId}
            className={`${styles.tab}${selected ? ` ${styles.tabActive}` : ''}`}
            onClick={() => onSelect(id)}
          >
            <span className={styles.tabIconBadge} aria-hidden>
              <Icon className={styles.tabIcon} />
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );
});

/**
 * Dashboard route: Financial Copilot (onboarding agent in copilot mode) or Finance Q&A (Talk to Reese).
 * Copilot mode uses the embedded onboarding agent with the same `v2_onboarding_ai_result.financial_profile` persistence
 * as full onboarding, including `welcome_message_answer` from Reese's opening question.
 *
 * Only the active tab is mounted so the two agents do not race on the same financial profile.
 * Both tabs share `useProfileDrawer`. On a wide screen the messages show first,
 * then the profile opens after 3s. Each new assistant reply waits 3s, then opens it again if it was closed.
 * The profile stays closed while the first-run "Use your location?" prompt is open.
 */
export default function CopilotPage() {
  const posthog = usePostHog();
  const [activeTab, setActiveTab] = useState(TAB_COPILOT);

  const selectTab = useCallback(
    (nextTab) => {
      if (nextTab === activeTab) return;
      capture(posthog, COPILOT_PAGE_EVENT, { action: 'tab_change', from: activeTab, to: nextTab });
      setActiveTab(nextTab);
    },
    [activeTab, posthog]
  );

  const tabBar = <CopilotTabBar activeTab={activeTab} onSelect={selectTab} />;
  const active = TABS.find((tab) => tab.id === activeTab) ?? TABS[0];

  return (
    <div className={styles.root}>
      <div className={styles.panel}>
        <div
          id={active.panelId}
          role="tabpanel"
          aria-labelledby={active.tabId}
          className={styles.panelContent}
        >
          {activeTab === TAB_COPILOT ? (
            <V2OnboardingAgent variant="copilot" topBar={tabBar} showScenarioCta />
          ) : (
            <TalkToReesePage embedded topBar={tabBar} showScenarioCta />
          )}
        </div>
      </div>
    </div>
  );
}
